import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../i18nContext';
import StaticPageLayout from './StaticPageLayout';
import { Building2, Users, Shield, Zap, HeadphonesIcon } from 'lucide-react';

interface Props { onBack: () => void; }

export default function EnterprisePage({ onBack }: Props) {
  const { language } = useLanguage();
  const en = language === 'en';

  const features = [
    { icon: <Users size={24} />, title: en ? 'Unlimited Team Members' : '无限团队成员', desc: en ? 'Add your entire organization with role-based access control.' : '添加整个组织，支持基于角色的访问控制。' },
    { icon: <Shield size={24} />, title: en ? 'Enterprise Security' : '企业级安全', desc: en ? 'SOC 2 compliance, SSO integration, and data encryption at rest.' : 'SOC 2 合规、SSO 集成、静态数据加密。' },
    { icon: <Zap size={24} />, title: en ? 'Priority Processing' : '优先处理', desc: en ? 'Dedicated infrastructure for faster transcription and analysis.' : '专属基础设施，更快的转录和分析速度。' },
    { icon: <HeadphonesIcon size={24} />, title: en ? 'Dedicated Support' : '专属支持', desc: en ? '24/7 priority support with a dedicated account manager.' : '7x24 小时优先支持，配备专属客户经理。' },
  ];

  return (
    <StaticPageLayout title={en ? 'Enterprise' : '企业版'} onBack={onBack}>
      <Helmet>
        <title>{en ? 'Enterprise - MinutesAI' : '企业版 - MinutesAI'}</title>
        <meta name="description" content={en ? 'MinutesAI Enterprise: unlimited team members, SOC 2 compliance, SSO, dedicated support, and priority processing for large organizations.' : 'MinutesAI 企业版：无限团队成员、SOC 2 合规、SSO 集成、专属支持，为大型组织提供优先处理能力。'} />
        <link rel="canonical" href="https://minutesai.com/enterprise" />
        <meta property="og:title" content={en ? 'Enterprise - MinutesAI' : '企业版 - MinutesAI'} />
        <meta property="og:url" content="https://minutesai.com/enterprise" />
      </Helmet>
      <div className="text-center mb-16">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-brand-900/10 mb-6">
          <Building2 size={32} className="text-brand-900" />
        </div>
        <h2 className="text-4xl font-bold text-brand-900 font-serif mb-4">
          {en ? 'Minutes AI for Enterprise' : 'Minutes AI 企业版'}
        </h2>
        <p className="text-lg text-ink-secondary max-w-2xl mx-auto">
          {en ? 'Scalable meeting intelligence for teams of all sizes. Secure, compliant, and built for the modern workplace.' : '为各种规模团队打造的可扩展会议智能方案。安全、合规，专为现代办公场景设计。'}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
        {features.map((f, i) => (
          <div key={i} className="bg-white rounded-2xl border border-surface-border p-8">
            <div className="text-brand-700 mb-4">{f.icon}</div>
            <h3 className="text-xl font-bold text-brand-900 mb-2">{f.title}</h3>
            <p className="text-ink-secondary">{f.desc}</p>
          </div>
        ))}
      </div>

      <div className="text-center bg-white rounded-2xl border border-surface-border p-12">
        <h3 className="text-2xl font-bold text-brand-900 font-serif mb-4">
          {en ? 'Get in Touch' : '联系我们'}
        </h3>
        <p className="text-ink-secondary mb-6">
          {en ? 'Contact our sales team for a custom quote tailored to your organization.' : '联系我们的销售团队，获取为您组织量身定制的报价方案。'}
        </p>
        <a href="mailto:enterprise@minutesai.com" className="inline-block bg-brand-900 text-white px-8 py-3 rounded-full font-bold hover:bg-brand-800 transition-colors">
          {en ? 'Contact Sales' : '联系销售'}
        </a>
      </div>
    </StaticPageLayout>
  );
}
